import React, { Component } from 'react';
import PropTypes from 'prop-types';
import has from 'lodash/has';

import { isEmpty, isNonEmptyString } from '../../../js/app/react/validators';
import { Select } from '../../CommonComponents/OPL/Select/Select/Select';

export const LEFT = 1;
export const CENTER = 2;
export const RIGHT = 3;

export function determineInputHTMLClass(focused, isUnder, focusedClassName, alignment, className) {
  const classNames = [
    'no-selection',
    'adv-workflow-input-placeholder',
    isUnder ? 'adv-workflow-input-placeholder-under' : 'adv-workflow-input-placeholder-over',
  ];

  if (isNonEmptyString(className)) {
    classNames.push(className.trim().split(/\s+/));
  }

  const ALIGNMENT_CLASSNAME_MAP = {
    [LEFT]: 'adv-workflow-input-placeholder-left',
    [CENTER]: 'adv-workflow-input-placeholder-center',
    [RIGHT]: 'adv-workflow-input-placeholder-right',
  };

  const FOCUSED_CLASSNAME_MAP = {
    [false]: '',
    [true]: [focusedClassName],
  };

  if (!has(ALIGNMENT_CLASSNAME_MAP, alignment)) {
    throw Error('Unexpected alignment');
  }

  if (!has(FOCUSED_CLASSNAME_MAP, focused)) {
    throw Error('Unexpected value for focus');
  }

  return classNames
    .concat(ALIGNMENT_CLASSNAME_MAP[alignment])
    .concat(FOCUSED_CLASSNAME_MAP[focused])
    .join(' ');
}

function determineInputHTMLStyle(alignment, paddingLeft, paddingRight) {
  switch (alignment) {
    case LEFT:
      if (paddingLeft === null) {
        return {};
      }
      return {
        left: `${paddingLeft}px`,
      };
    case CENTER:
      return {
        left: '50%',
      };
    case RIGHT:
      if (paddingRight === null) {
        return {};
      }
      return {
        right: `${paddingRight}px`,
      };
    default:
      throw Error('Unexpected alignment');
  }
}

export function buildGroupedSelectOptions(options, groups) {
  if (!groups || groups.length === 0) {
    return options;
  }

  const groupsMap = {};
  groups.forEach(({ groupId, title, ...rest }) => {
    groupsMap[groupId] = {
      ...rest,
      label: title,
      options: [],
    };
  });
  options.forEach((option) => {
    if (groupsMap[option.groupId]) {
      groupsMap[option.groupId].options.push(option);
    }
  });

  return Object.values(groupsMap).filter((group) => group.options.length > 0);
}

export function normalizeSelectValue(value) {
  if (value === null || value === '' || isEmpty(value)) {
    return null;
  }

  return value;
}

const EMPTY_HANDLER = () => {};

class WorkflowSimpleSelect extends Component {
  constructor() {
    super();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  onFocus(event) {
    this.props.onFocus(event);
  }

  onBlur(event) {
    this.props.onBlur(event);
  }

  render() {
    const {
      value,
      placeholder,
      className,
      placeholderClassName,
      alignment,
      focusedClassName,
      options,
      groups,
      onValueChange,
      isLeftAligned,
      isUnder,
      id,
    } = this.props;
    const computedAlignment = alignment || (isLeftAligned ? LEFT : CENTER);
    const selectOptions = buildGroupedSelectOptions(options, groups);

    return (
      <div className="adv-workflow-simple-select">
        <div
          className={determineInputHTMLClass(
            !isEmpty(value),
            isUnder,
            focusedClassName,
            computedAlignment,
            placeholderClassName,
          )}
          style={determineInputHTMLStyle(computedAlignment, null, null)}
          aria-label={placeholder}
          id={id}
        >
          {placeholder}
        </div>
        <Select
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          openMenuOnFocus
          placeholder=""
          value={normalizeSelectValue(value)}
          options={selectOptions}
          onChange={onValueChange}
          className={`adv-workflow-opl-select ${className}`.trim()}
          aria-labelledby={id}
        />
      </div>
    );
  }
}
WorkflowSimpleSelect.defaultProps = {
  isUnder: false,
  value: '',
  onFocus: EMPTY_HANDLER,
  onBlur: EMPTY_HANDLER,
  className: '',
  placeholderClassName: '',
  focusedClassName: 'adv-workflow-input-placeholder-focused',
  isLeftAligned: false,
  id: undefined,
  groups: null,
};

WorkflowSimpleSelect.propTypes = {
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  focusedClassName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({}), PropTypes.string]),
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onValueChange: PropTypes.func.isRequired,
  isLeftAligned: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  isUnder: PropTypes.bool,
  id: PropTypes.string,
  alignment: PropTypes.oneOf([LEFT, CENTER, RIGHT]),
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({})),
};

export default WorkflowSimpleSelect;
